import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api } from "../../lib/api";

const ROLE_OPTIONS = [
  { value: "news_event_admin", label: "News & Events Admin", help: "Can create, edit and publish News and Events only." },
  { value: "editor", label: "Editor", help: "Can edit general CMS content where allowed." },
  { value: "publisher", label: "Publisher", help: "Can publish and remove approved content where allowed." },
  { value: "admin", label: "Admin", help: "Broad CMS access except Super Admin-only user management." },
  { value: "super_admin", label: "Super Admin", help: "Full system access including admin users, backups and security." },
];

export default function AdminUserForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "news_event_admin", status: "active" });
  const [resetPassword, setResetPassword] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!id) return;
    api(`/api/admin/admin-users/${id}`).then((user) => setForm({ name: user.name || "", email: user.email || "", password: "", role: user.role || "editor", status: user.status || "active" })).catch((err) => setError(err.message));
  }, [id]);

  function update(e) { setForm({ ...form, [e.target.name]: e.target.value }); }

  async function submit(e) {
    e.preventDefault();
    setError("");
    try {
      const payload = { ...form };
      if (id) delete payload.password;
      await api(id ? `/api/admin/admin-users/${id}` : "/api/admin/admin-users", { method: id ? "PUT" : "POST", body: JSON.stringify(payload) });
      navigate("/adxm/admin-users");
    } catch (err) { setError(err.message); }
  }

  async function submitReset(e) {
    e.preventDefault();
    setError(""); setMessage("");
    try {
      await api(`/api/admin/admin-users/${id}/password`, { method: "PUT", body: JSON.stringify({ password: resetPassword }) });
      setResetPassword("");
      setMessage("Password reset successfully.");
    } catch (err) { setError(err.message); }
  }

  return (
    <AdminLayout title={id ? "Edit Admin User" : "Create Admin User"} subtitle="Use strong passwords and assign the lowest role needed.">
      {error && <div className="mb-4 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}
      {message && <div className="mb-4 rounded-xl bg-emerald-50 p-4 font-semibold text-emerald-700">{message}</div>}
      <form onSubmit={submit} className="max-w-3xl space-y-5 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <input name="name" value={form.name} onChange={update} placeholder="Full name" className="w-full rounded-xl border border-slate-200 p-3" required />
        <input name="email" type="email" value={form.email} onChange={update} placeholder="Email" className="w-full rounded-xl border border-slate-200 p-3" required />
        {!id && <input name="password" type="password" value={form.password} onChange={update} placeholder="Temporary password" className="w-full rounded-xl border border-slate-200 p-3" required minLength="8" />}
        <div className="grid gap-4 md:grid-cols-2">
          <select name="role" value={form.role} onChange={update} className="rounded-xl border border-slate-200 p-3">
            {ROLE_OPTIONS.map((role) => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>
          <select name="status" value={form.status} onChange={update} className="rounded-xl border border-slate-200 p-3">
            <option value="active">Active</option>
            <option value="blocked">Blocked</option>
          </select>
        </div>
        <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm text-slate-700">
          <strong className="block text-blue-900">Selected role:</strong>
          {ROLE_OPTIONS.find((role) => role.value === form.role)?.help}
        </div>
        <button className="rounded-xl bg-emerald-700 px-6 py-3 font-bold text-white">Save Admin User</button>
      </form>

      {id && (
        <form onSubmit={submitReset} className="mt-6 max-w-3xl space-y-4 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <h2 className="text-xl font-black text-slate-950">Reset Password</h2>
          <input type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} placeholder="New password" className="w-full rounded-xl border border-slate-200 p-3" minLength="8" />
          <button className="rounded-xl bg-slate-900 px-6 py-3 font-bold text-white">Reset Password</button>
        </form>
      )}
    </AdminLayout>
  );
}
